import clsx from 'clsx';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { setDialogIsOpen, setShowroomShareMediaQuality } from 'Apollo/Store/Showroom/UserView/Share/resolvers';
import { setSelectedPdfs, setShowroomSelectedMedia } from 'Apollo/Store/Showroom/UserView/resolvers';
import { Media, MediaType, QualityOptions, useGetMobileMenuOpenedQuery, useGetShowroomSelectedTabQuery } from 'generated/graphql';

import { useGetCurrentShowroomData, useIsShowroomDowloadOptionEnabled, useIsShowroomMobileVersion, useIsShowroomUserView } from 'lib/showroom';

import AppBar from '@material-ui/core/AppBar';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DownloadIcon from '@material-ui/icons/SaveAlt';

import { ReactComponent as ShowroomFilterIcon } from 'icons/showroomFilterIcon.svg';

import { useStyles } from './styles';

export type ComponentProps = {
	selectedSection: number;
	showFilters: boolean;
	onShowFilters: () => void;
	loading: boolean;
	filteredMedias: Media[];
	hasFilters: boolean;
};

const ShowroomSectionActions = (props: ComponentProps) => {
	const { selectedSection, showFilters, onShowFilters, loading, filteredMedias, hasFilters } = props;

	const { t } = useTranslation();

	const showroom = useGetCurrentShowroomData();
	const isMobileVersion = useIsShowroomMobileVersion();
	const isUserView = useIsShowroomUserView();
	const downloadEnabled = useIsShowroomDowloadOptionEnabled();

	const {
		data: {
			showroom: {
				userView: { activeMediaMobileMenu }
			}
		}
	} = useGetMobileMenuOpenedQuery();

	const {
		data: {
			showroom: {
				userView: { selectedTab }
			}
		}
	} = useGetShowroomSelectedTabQuery();

	const [selectedAction, setSelectedAction] = React.useState<number | boolean>(false);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [filterMediasBy, setFilterMediasBy] = React.useState<MediaType | 'all'>(null);

	const videoMedias = React.useMemo(() => filter(filteredMedias, { mediaType: MediaType.Video }), [filteredMedias]);
	const imageMedias = React.useMemo(() => filter(filteredMedias, { mediaType: MediaType.Image }), [filteredMedias]);
	const pdfMedias = React.useMemo(() => filter(filteredMedias, { mediaType: MediaType.Pdf }), [filteredMedias]);
	const showroomPdfs = React.useMemo(() => showroom.pdfs || [], [showroom]);
	const canDisplayDownloadOption = React.useMemo(
		() => downloadEnabled && isUserView && filteredMedias.length > 0,
		[downloadEnabled, isUserView, filteredMedias]
	);
	const canDisplayDownloadPdfOption = React.useMemo(() => showroom.downloadPdfEnabled && !isEmpty(showroomPdfs), [showroom, showroomPdfs]);

	const optionAvailable = React.useMemo(() => {
		const mediaTypes = [!isEmpty(imageMedias), !isEmpty(videoMedias), !isEmpty(pdfMedias)];
		const availableMediaCount = mediaTypes.filter(Boolean).length;

		if (availableMediaCount === 1) {
			if (!isEmpty(imageMedias)) return 'images';
			if (!isEmpty(videoMedias)) return 'videos';
			if (!isEmpty(pdfMedias)) return 'pdfs';
		}
		return 'all';
	}, [imageMedias, videoMedias, pdfMedias]);

	const classes = useStyles({
		uniqueMediaType: optionAvailable !== 'all',
		canDownloadPdfs: canDisplayDownloadPdfOption,
		isMobile: isMobileVersion
	});

	React.useEffect(() => {
		if (!anchorEl) setSelectedAction(false);
	}, [anchorEl]);

	const handleShareMedias = React.useCallback(
		(mediaQuality: QualityOptions) => {
			setAnchorEl(null);
			let medias = filteredMedias;
			if (filterMediasBy && filterMediasBy !== 'all') medias = filter(medias, { mediaType: filterMediasBy });
			if (filterMediasBy && filterMediasBy === 'all' && canDisplayDownloadPdfOption) setSelectedPdfs(showroomPdfs);
			setShowroomSelectedMedia(medias as any);
			setShowroomShareMediaQuality(mediaQuality);
			setFilterMediasBy(null);
			setDialogIsOpen(true);
		},
		[canDisplayDownloadPdfOption, filterMediasBy, filteredMedias, showroomPdfs]
	);

	const qualityOptions = React.useMemo(
		() => [
			<MenuItem
				key="showroomDownloadItemOptionScreen"
				id="showroomDownloadItemOptionScreen"
				onClick={() => handleShareMedias(QualityOptions.Screen)}
				className={clsx({
					[classes.showroomDownloadMenuSubItem]: optionAvailable === 'all',
					[classes.showroomDownloadMenuItem]: !isMobileVersion,
					[classes.showroomDownloadMenuItemMobile]: isMobileVersion
				})}
			>
				{t('showroom.download.menu.subOptions.screen')}
			</MenuItem>,
			<Divider variant="middle" key="sumbenuDividier" />,
			<MenuItem
				key="showroomDownloadItemOptionOriginal"
				id="showroomDownloadItemOptionOriginal"
				onClick={() => handleShareMedias(QualityOptions.Original)}
				className={clsx({
					[classes.showroomDownloadMenuSubItem]: optionAvailable === 'all',
					[classes.showroomDownloadMenuItem]: !isMobileVersion,
					[classes.showroomDownloadMenuItemMobile]: isMobileVersion
				})}
			>
				{t('showroom.download.menu.subOptions.hd')}
			</MenuItem>
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, isMobileVersion, optionAvailable, selectedSection, handleShareMedias]
	);

	const createMenuItem = React.useCallback(
		(key: string, id: string, onClick: () => void, text: string, disabled = false) => (
			<MenuItem
				key={key}
				id={id}
				onClick={onClick}
				className={clsx({
					[classes.showroomDownloadMenuItem]: !isMobileVersion,
					[classes.showroomDownloadMenuItemMobile]: isMobileVersion
				})}
				disabled={disabled}
			>
				{text}
			</MenuItem>
		),
		[classes, isMobileVersion]
	);

	const downloadMenuOptions = React.useMemo(() => {
		const menuItems = [];
		if (optionAvailable !== 'all' && optionAvailable !== 'pdfs' && !canDisplayDownloadPdfOption) return qualityOptions;
		if (canDisplayDownloadPdfOption)
			menuItems.push(
				createMenuItem(
					'showroomDownloadPdfs',
					'showroomDownloadPdfs',
					() => {
						setAnchorEl(null);
						setSelectedPdfs(showroomPdfs);
						setDialogIsOpen(true);
					},
					t('showroom.download.menu.options.pressKits')
				),
				<Divider key="showroomDownloadItemAllPressKitsDivider" className={classes.showroomDownloadMenuDivider} variant="inset" />
			);
		if (!isEmpty(imageMedias)) {
			menuItems.push(
				createMenuItem(
					'showroomDownloadItemImage',
					'showroomDownloadItemImage',
					() => setFilterMediasBy(filterMediasBy === MediaType.Image ? null : MediaType.Image),
					t('showroom.download.menu.options.images'),
					isEmpty(imageMedias)
				),
				<Divider key="showroomDownloadItemAllDivider" className={classes.showroomDownloadMenuDivider} variant="inset" />,
				<Collapse key="showroomDownloadItemImageCollapse" in={filterMediasBy === MediaType.Image}>
					{qualityOptions}
				</Collapse>
			);
		}
		if (!isEmpty(videoMedias)) {
			menuItems.push(
				createMenuItem(
					'showroomDownloadItemVideo',
					'showroomDownloadItemVideo',
					() => setFilterMediasBy(filterMediasBy === MediaType.Video ? null : MediaType.Video),
					t('showroom.download.menu.options.videos')
				),
				<Divider key="showroomDownloadItemVideoDivider" className={classes.showroomDownloadMenuDivider} variant="inset" />,
				<Collapse key="showroomDownloadItemVideoCollapse" in={filterMediasBy === MediaType.Video}>
					{qualityOptions}
				</Collapse>
			);
		}
		if (!isEmpty(pdfMedias)) {
			menuItems.push(
				createMenuItem(
					'showroomDownloadPdfFiles',
					'showroomDownloadPdfFiles',
					() => {
						setAnchorEl(null);
						setShowroomSelectedMedia(pdfMedias);
						setShowroomShareMediaQuality(QualityOptions.Original);
						setDialogIsOpen(true);
					},
					t('showroom.download.menu.options.pdfs')
				)
			);
			if (optionAvailable !== 'pdfs') {
				menuItems.push(<Divider key="showroomDownloadItemPdfDivider" className={classes.showroomDownloadMenuDivider} variant="inset" />);
			}
		}
		if (optionAvailable !== 'pdfs')
			menuItems.push(
				createMenuItem(
					'showroomDownloadItemAll',
					'showroomDownloadItemAll',
					() => setFilterMediasBy(filterMediasBy === 'all' ? null : 'all'),
					t('showroom.download.menu.options.all'),
					isEmpty(videoMedias) && isEmpty(imageMedias) && isEmpty(pdfMedias)
				),
				<Collapse key="showroomDownloadItemAllCollapse" in={filterMediasBy === 'all'}>
					{qualityOptions}
				</Collapse>
			);
		return menuItems;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canDisplayDownloadPdfOption, filterMediasBy, imageMedias, isMobileVersion, optionAvailable, qualityOptions, showroomPdfs, t, videoMedias]);

	const downloadMediaActionButton = React.useMemo(
		() => (
			<>
				<div
					className={classes.showroomActionBox}
					onClick={ev =>
						isMobileVersion ? setAnchorEl(ev.currentTarget.parentNode.parentNode.parentElement) : setAnchorEl(ev.currentTarget)
					}
					id={'showroomDownloadButton'}
				>
					<DownloadIcon fontSize="small" className={classes.showroomActionBoxIcon} />
					{t('showroom.download.menu.label.all')}
					{!isMobileVersion && <ArrowDropDownIcon />}
				</div>
				<Menu
					id="showroomDownloadMenu"
					classes={{
						paper: clsx({
							[classes.showroomDownloadMenu]: !isMobileVersion,
							[classes.showroomDownloadMenuMobile]: isMobileVersion,
							[classes.showroomDownloadMenuMobileSubmenuOpened]: isMobileVersion && !!filterMediasBy,
							[classes.showroomDownloadMenuMobileNoMedia]: isMobileVersion && optionAvailable !== 'all'
						})
					}}
					anchorEl={anchorEl}
					getContentAnchorEl={null}
					keepMounted
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: isMobileVersion ? 'center' : 'right'
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: isMobileVersion ? 'center' : 'right'
					}}
					open={Boolean(anchorEl)}
					onClose={() => {
						setAnchorEl(null);
						setFilterMediasBy(null);
					}}
				>
					{downloadMenuOptions}
				</Menu>
			</>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, isMobileVersion, filterMediasBy, optionAvailable, anchorEl, downloadMenuOptions]
	);

	const displayFilterButton = React.useMemo(() => {
		if (loading && !isMobileVersion) return null;
		return (
			<div className={classes.showroomActionBox} onClick={onShowFilters} id="showroomShowFiltersButton">
				<ShowroomFilterIcon className={classes.showroomActionBoxIcon} style={{ paddingTop: 2 }} />
				{isMobileVersion
					? t('showroom.filter.button.label.mobile')
					: showFilters
					? t('showroom.filter.button.label.hide')
					: t('showroom.filter.button.label.show')}
			</div>
		);
	}, [classes, showFilters, loading, isMobileVersion, onShowFilters, t]);

	const bottomStickyBar = React.useMemo(
		() => {
			if (!canDisplayDownloadOption && !hasFilters) return;
			return (
				<AppBar
					position="fixed"
					id="showroomMobileStickyBar"
					className={clsx(classes.stickyBar, {
						[classes.stickyBarMobileOpened]: isMobileVersion && !!anchorEl,
						[classes.stickyBarMobileOpenedNoMedia]: isMobileVersion && !!anchorEl && optionAvailable !== 'all',
						[classes.stickyBarMobileOpenedSubmenuOpened]: isMobileVersion && !!anchorEl && !!filterMediasBy
					})}
				>
					<Tabs
						value={selectedAction}
						onChange={(event: React.ChangeEvent<{}>, newValue: number) => setSelectedAction(newValue)}
						variant="fullWidth"
						TabIndicatorProps={{
							style: {
								display: 'none'
							}
						}}
					>
						{canDisplayDownloadOption && (
							<>
								<Tab
									label={downloadMediaActionButton}
									fullWidth
									classes={{
										root: classes.showroomActionTab,
										textColorInherit: classes.showroomActionTextMobile
									}}
								/>
								<Divider variant="middle" orientation="vertical" flexItem className={classes.mobileButtonDivider} />
							</>
						)}
						{!anchorEl && hasFilters && (
							<Tab
								label={displayFilterButton}
								fullWidth
								classes={{
									root: classes.showroomActionTab,
									textColorInherit: classes.showroomActionTextMobile
								}}
							/>
						)}
					</Tabs>
				</AppBar>
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			canDisplayDownloadOption,
			hasFilters,
			isMobileVersion,
			anchorEl,
			optionAvailable,
			filterMediasBy,
			selectedAction,
			isUserView,
			downloadMediaActionButton,
			displayFilterButton
		]
	);

	return (
		<div className={classes.showroomActionsContainer}>
			{!isMobileVersion && (
				<div className={classes.actionContainer}>
					{hasFilters && displayFilterButton}
					{canDisplayDownloadOption && downloadMediaActionButton}
				</div>
			)}
			{isMobileVersion && !activeMediaMobileMenu && !selectedTab && bottomStickyBar}
		</div>
	);
};

export default React.memo(ShowroomSectionActions);
